import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { useTranslation } from 'react-i18next';
import { getDates } from 'helpers/utils';
import dayjs from 'dayjs';
import { useAppContext } from 'providers/AppProvider';
import { TooltipComponent } from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CallbackDataParams } from 'echarts/types/dist/shared';
echarts.use([TooltipComponent, BarChart]);

interface DailyCount {
  date: string;
  count: number;
  amount: number;
}

interface EcomTotalOrdersChartProps {
  data?: DailyCount[];
  showAmount?: boolean;
}

const getDefaultOptions = (
  getThemeColor: (name: string) => string, 
  t: any,
  chartData?: DailyCount[],
  showAmount: boolean = false
) => {
  // Use provided data if available, or fallback to default sample data
  const dates = chartData && chartData.length > 0 
    ? chartData.map(item => item.date) 
    : getDates(new Date('5/1/2022'), new Date('5/7/2022'), 1000 * 60 * 60 * 24);
  
  const values = chartData && chartData.length > 0
    ? chartData.map(item => showAmount ? item.amount : item.count)
    : [120, 200, 150, 80, 70, 110, 120];

  return {
    color: showAmount ? getThemeColor('success') : getThemeColor('primary'),
    tooltip: {
      trigger: 'item',
      padding: [7, 10],
      backgroundColor: getThemeColor('body-highlight-bg'),
      borderColor: getThemeColor('border-color'),
      textStyle: { color: getThemeColor('light-text-emphasis') },
      borderWidth: 1,
      transitionDuration: 0,
      formatter: (params: CallbackDataParams) =>
        `<strong>${dayjs(params.name).format('DD MMM YYYY')}:</strong> ${params.value}${showAmount ? ' ' + t('transactionSummary.dinarSymbol') : ' ' + t('orders')}`
    },
    xAxis: {
      type: 'category',
      data: dates,
      show: true,
      boundaryGap: false,
      axisLine: {
        show: true,
        lineStyle: { color: getThemeColor('secondary-bg') }
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        formatter: (value: string) => dayjs(value).format('DD MMM'),
        interval: 6,
        showMinLabel: true,
        showMaxLabel: true,
        color: getThemeColor('secondary-color')
      }
    },
    yAxis: {
      show: false,
      type: 'value',
      boundaryGap: false
    },
    series: [
      {
        type: 'bar',
        barWidth: '5px',
        data: values,
        showBackground: true,
        symbol: 'none',
        itemStyle: {
          borderRadius: 10
        },
        backgroundStyle: {
          borderRadius: 10,
          color: getThemeColor('primary-bg-subtle')
        }
      }
    ],
    grid: { right: 10, left: 10, bottom: 0, top: 0 }
  };
};

const EcomTotalOrdersChart = ({ data, showAmount = false }: EcomTotalOrdersChartProps) => {
  const { getThemeColor } = useAppContext();
  const { t } = useTranslation();

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={getDefaultOptions(getThemeColor, t, data, showAmount)}
      style={{ height: '85px', width: '115px' }}
    />
  );
};

export default EcomTotalOrdersChart;
